// src/App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import Header from "./components/Header";
import TaskSidebar from "./components/TaskSidebar";
import DriverSidebar from "./components/DriverSidebar";
import Map from "./components/Map";
import TaskDetailsModal from "./components/TaskDetailsModal";
import DriverDetailsModal from "./components/DriverDetailsModal";
import Login from "./components/Login";
import DispatchDashboard from "./components/DispatchDashboard";
import NotificationSystem from "./components/NotificationSystem";
import PinAuthentication from "./components/PinAuthentication";
import { format, addDays } from "date-fns";
import "./styles/App.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

const ProtectedApp = () => {
  const [tasks, setTasks] = useState({
    nonaccepter: [],
    unassigned: [],
    assigned: [],
    prete: [],
    completed: [],
  });
  const [drivers, setDrivers] = useState({
    free: [],
    busy: [],
    inactive: [],
    externe: [],
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: addDays(new Date(), 1),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTaskSidebarVisible, setIsTaskSidebarVisible] = useState(true);
  const [isDriverSidebarVisible, setIsDriverSidebarVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskOrderId, setSelectedTaskOrderId] = useState(null);
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const modalRef = useRef(null);
  const isFetchingTasks = useRef(false);
  const navigate = useNavigate();

  const API_KEY = "b6c8b023-5992-4d5a-a366-265a6703c3fc";
  const EMAIL_DISPATCH = "dispatch@lalivraison.ma";
  const POLLING_INTERVAL = 10000;
  const REQUEST_TIMEOUT = 15000;
  const MAX_RETRIES = 2;

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchTasks = async (retryCount = 0) => {
    if (isFetchingTasks.current) return;

    isFetchingTasks.current = true;
    try {
      const taskParams = new URLSearchParams();
      const mindate = format(dateRange.startDate, "yyyy-MM-dd");
      const maxdate = format(dateRange.endDate, "yyyy-MM-dd");
      taskParams.append("mindate", mindate);
      taskParams.append("maxdate", maxdate);
      taskParams.append("api_key", API_KEY);
      taskParams.append("email_dispatch", EMAIL_DISPATCH);

      const taskResponse = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/getAllCommandev2.php",
        taskParams,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          timeout: REQUEST_TIMEOUT,
        }
      );

      const transformedTasks = {
        nonaccepter: [],
        unassigned: [],
        assigned: [],
        prete: [],
        completed: [],
      };

      if (Array.isArray(taskResponse.data)) {
        taskResponse.data.forEach((task) => {
          const taskWithId = { ...task, id: task.idcmd };
          const createDate = new Date(task.created_at);
          const currentDate = new Date();
          const timeDifference = currentDate - createDate;
          taskWithId.minDifferencecreate =
            Math.floor(timeDifference / (1000 * 60)) + " min";

          if (
            task.status_commande == 0 &&
            task.status_delivery == "NON ASSIGNE" &&
            task.status_pickup == "NON ASSIGNE"
          ) {
            transformedTasks.nonaccepter.push(taskWithId);
          } else if (
            task.status_commande == 1 &&
            task.status_pickup == "NON ASSIGNE" &&
            task.status_delivery == "NON ASSIGNE" &&
            task.prete == 0
          ) {
            transformedTasks.unassigned.push(taskWithId);
          } else if (
            task.status_commande == 1 &&
            task.status_pickup == "NON ASSIGNE" &&
            task.status_delivery == "NON ASSIGNE" &&
            task.prete == 1
          ) {
            transformedTasks.prete.push(taskWithId);
          } else if (
            (task.status_pickup == "SUCCESS" &&
              task.status_delivery == "SUCCESS") ||
            task.status_pickup == "FAILED" ||
            task.status_delivery == "FAILED"
          ) {
            transformedTasks.completed.push(taskWithId);
          } else if (
            (task.status_commande == 1 || task.status_commande == 4) &&
            ((task.status_pickup != "NON ASSIGNE" &&
              task.status_delivery != "NON ASSIGNE") ||
              (task.status_pickup != "SUCCESS" &&
                task.status_delivery != "SUCCESS")) &&
            task.prete == 0
          ) {
            transformedTasks.assigned.push(taskWithId);
          } else {
            console.warn(`Task ${task.idcmd} does not match any category:`, task);
            transformedTasks.completed.push(taskWithId);
          }
        });
      } else {
        console.error("Task API response is not an array:", taskResponse.data);
        setError("Invalid task data format");
      }

      setTasks(transformedTasks);
      setError(null);
    } catch (err) {
      console.error("Task fetch error:", err.message);
      if (err.code === "ECONNABORTED" && retryCount < MAX_RETRIES) {
        await delay(1000);
        return fetchTasks(retryCount + 1);
      }
      setError(`Failed to fetch tasks: ${err.message}`);
    } finally {
      isFetchingTasks.current = false;
    }
  };

  const fetchDrivers = async () => {
    try {
      const driverParams = new URLSearchParams();
      driverParams.append("restaurant_id", 403);
      driverParams.append("email_dispatch", EMAIL_DISPATCH);
      driverParams.append("api_key", API_KEY);

      const driverResponse = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/getFullLivreurs.php",
        driverParams,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          timeout: REQUEST_TIMEOUT,
        }
      );

      const transformedDrivers = {
        free: [],
        busy: [],
        inactive: [],
        externe: [],
      };

      if (Array.isArray(driverResponse.data)) {
        driverResponse.data.forEach((driver) => {
          if (!driver.uuid || !driver.nom) {
            console.warn("Skipping driver with missing uuid or name:", driver);
            return;
          }

          const lat = parseFloat(driver.lat);
          const lng = parseFloat(driver.lng);
          if (isNaN(lat) || isNaN(lng)) {
            console.warn(
              `Skipping driver ${driver.nom} due to invalid coordinates: Lat=${driver.lat}, Lng=${driver.lng}`
            );
            return;
          }

          const cleanName =
            driver.nom?.split(" - Solde livreur")[0]?.trim() || "Unknown Driver";
          const driverWithId = { ...driver, id: driver.uuid, name: cleanName };

          if (
            driver.type &&
            driver.type.trim() !== "" &&
            ["Livry", "Glovo", "Yassir"].includes(driver.type)
          ) {
            transformedDrivers.externe.push(driverWithId);
          } else if (driver.connected == 1 && driver.status == "FREE") {
            transformedDrivers.free.push(driverWithId);
          } else if (driver.connected == 1 && driver.status == "BUSY") {
            transformedDrivers.busy.push(driverWithId);
          } else {
            transformedDrivers.inactive.push(driverWithId);
          }
        });
      } else {
        console.error("Driver API response is not an array:", driverResponse.data);
        setError("Invalid driver data format");
      }

      setDrivers(transformedDrivers);
    } catch (err) {
      console.error("Driver fetch error:", err.message);
      setError("Failed to fetch drivers: " + err.message);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchDrivers(), fetchTasks()]);
    setLoading(false);
  };

  const updateTaskOptimistically = (orderId, newCategory, updatedTaskData) => {
    setTasks((prevTasks) => {
      const updatedTasks = { ...prevTasks };
      let taskToUpdate = null;
      let sourceCategory = null;

      Object.keys(prevTasks).forEach((category) => {
        const foundTask = prevTasks[category].find((task) => task.order_id === orderId);
        if (foundTask) {
          taskToUpdate = foundTask;
          sourceCategory = category;
        }
      });

      if (!taskToUpdate) {
        console.warn(`Task with order_id ${orderId} not found for optimistic update`);
        return prevTasks;
      }

      updatedTasks[sourceCategory] = updatedTasks[sourceCategory].filter(
        (task) => task.order_id !== orderId
      );

      updatedTasks[newCategory] = [
        ...updatedTasks[newCategory],
        { ...taskToUpdate, ...updatedTaskData },
      ];

      return updatedTasks;
    });
  };

  const updateDriverOptimistically = (driverUuid, updatedData) => {
    setDrivers((prevDrivers) => {
      const updatedDrivers = { ...prevDrivers };
      let driverToUpdate = null;
      let sourceCategory = null;

      Object.keys(prevDrivers).forEach((category) => {
        const foundDriver = prevDrivers[category].find(
          (driver) => driver.uuid === driverUuid
        );
        if (foundDriver) {
          driverToUpdate = foundDriver;
          sourceCategory = category;
        }
      });

      if (!driverToUpdate) {
        console.warn(`Driver with uuid ${driverUuid} not found for optimistic update`);
        return prevDrivers;
      }

      updatedDrivers[sourceCategory] = updatedDrivers[sourceCategory].filter(
        (driver) => driver.uuid !== driverUuid
      );

      let newCategory;
      if (updatedData.type && ["Livry", "Glovo", "Yassir"].includes(updatedData.type)) {
        newCategory = "externe";
      } else if (updatedData.connected == 1 && updatedData.status == "FREE") {
        newCategory = "free";
      } else if (updatedData.connected == 1 && updatedData.status == "BUSY") {
        newCategory = "busy";
      } else {
        newCategory = "inactive";
      }

      updatedDrivers[newCategory] = [
        ...updatedDrivers[newCategory],
        { ...driverToUpdate, ...updatedData },
      ];

      return updatedDrivers;
    });
  };

  useEffect(() => {
    fetchData();

    const taskInterval = setInterval(() => {
      fetchTasks();
    }, POLLING_INTERVAL);

    const driverInterval = setInterval(fetchDrivers, POLLING_INTERVAL);

    return () => {
      clearInterval(taskInterval);
      clearInterval(driverInterval);
    };
  }, [dateRange]);

  const handleOpenModal = (orderId) => {
    setSelectedTaskOrderId(orderId);
    setIsModalOpen(true);
    modalRef.current = orderId;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTaskOrderId(null);
    modalRef.current = null;
  };

  const handleRefreshTasks = () => {
    fetchTasks();
  };

  const handleLogout = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.email) {
        const params = new URLSearchParams();
        params.append("email", user.email);
        params.append("email_dispatch", EMAIL_DISPATCH);
        params.append("api_key", API_KEY);

        await axios.post(
          "https://lalivraison.ma/Livreurxcs/Dispatch/logout.php",
          params,
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            timeout: 10000,
          }
        );
      }
    } catch (err) {
      console.error("Logout error:", err.message);
    } finally {
      localStorage.removeItem("remember_token");
      localStorage.removeItem("user");
      navigate("/login", { replace: true });
    }
  };

  const handleDrawLine = useCallback((taskData) => {
    document.querySelector(".map-container")?.dispatchEvent(
      new CustomEvent("drawLine", { detail: taskData })
    );
  }, []);

  const handleRemoveLines = useCallback(() => {
    document.querySelector(".map-container")?.dispatchEvent(
      new CustomEvent("removeLines")
    );
  }, []);

  const handleDriverClick = useCallback((driverData) => {
    // Focus on driver in map
    const mapContainer = document.querySelector(".map-container");
    if (mapContainer) {
      const event = new CustomEvent("centerOnDriver", { detail: driverData });
      mapContainer.dispatchEvent(event);
    }
  }, []);

  const handleDriverModalOpen = useCallback((driverData) => {
    setSelectedDriver(driverData.driver);
    setIsDriverModalOpen(true);
  }, []);

  const handleDriverModalClose = useCallback(() => {
    setIsDriverModalOpen(false);
    setSelectedDriver(null);
  }, []);

  const handleDashboardToggle = useCallback(() => {
    setIsPinModalOpen(true);
  }, []);

  const handlePinSuccess = useCallback(() => {
    setIsDashboardOpen(true);
  }, []);

  const handlePinClose = useCallback(() => {
    setIsPinModalOpen(false);
  }, []);


  return (
    <div className="app">
      <Header 
        setDateRange={setDateRange} 
        onLogout={handleLogout} 
        onDashboardToggle={handleDashboardToggle}
        tasks={tasks}
        drivers={drivers}
        onTaskClick={handleOpenModal}
        onDriverClick={handleDriverModalOpen}
      />
      
      {/* Enhanced Dispatch Dashboard Modal */}
      <DispatchDashboard 
        tasks={tasks} 
        drivers={drivers} 
        onRefresh={fetchData} 
        dateRange={dateRange}
        isOpen={isDashboardOpen}
        onClose={() => setIsDashboardOpen(false)}
      />
      
      {/* PIN Authentication Modal */}
      <PinAuthentication
        isOpen={isPinModalOpen}
        onClose={handlePinClose}
        onSuccess={handlePinSuccess}
      />
      
      <div className={`main-content ${!isTaskSidebarVisible && !isDriverSidebarVisible ? 'both-hidden' : ''}`}>
        {isTaskSidebarVisible && (
          <TaskSidebar
            tasks={tasks}
            isVisible={isTaskSidebarVisible}
            setIsVisible={setIsTaskSidebarVisible}
            onTaskClick={handleOpenModal}
            onAssignSuccess={handleRefreshTasks}
            onDrawLine={handleDrawLine}
            onRemoveLines={handleRemoveLines}
            updateTaskOptimistically={updateTaskOptimistically}
            updateDriverOptimistically={updateDriverOptimistically}
          />
        )}
        <button
          className={`minimize-button task-minimize-button ${
            isTaskSidebarVisible ? "visible" : "hidden"
          }`}
          onClick={() => {
            setIsTaskSidebarVisible(!isTaskSidebarVisible);
            // Dispatch event to notify map of sidebar change
            setTimeout(() => {
              document.querySelector(".map-container")?.dispatchEvent(
                new CustomEvent("sidebarToggle")
              );
            }, 100);
          }}
        >
          <i
            className={`fas ${
              isTaskSidebarVisible ? "fa-chevron-left" : "fa-chevron-right"
            }`}
          ></i>
        </button>
        
        <div className={`map-container ${!isTaskSidebarVisible && !isDriverSidebarVisible ? 'full-width' : ''}`}>
          <Map 
            tasks={tasks} 
            drivers={drivers} 
            onTaskClick={handleOpenModal}
            onDriverClick={handleDriverModalOpen}
          />
        </div>
        {isDriverSidebarVisible && (
          <DriverSidebar
            drivers={drivers}
            isVisible={isDriverSidebarVisible}
            setIsVisible={setIsDriverSidebarVisible}
            fetchDrivers={fetchDrivers}
            onDriverClick={handleDriverClick}
            updateDriverOptimistically={updateDriverOptimistically}
          />
        )}
        <button
          className={`minimize-button driver-minimize-button ${
            isDriverSidebarVisible ? "visible" : "hidden"
          }`}
          onClick={() => {
            setIsDriverSidebarVisible(!isDriverSidebarVisible);
            // Dispatch event to notify map of sidebar change
            setTimeout(() => {
              document.querySelector(".map-container")?.dispatchEvent(
                new CustomEvent("sidebarToggle")
              );
            }, 100);
          }}
        >
          <i
            className={`fas ${
              isDriverSidebarVisible ? "fa-chevron-right" : "fa-chevron-left"
            }`}
          ></i>
        </button>
      </div>
      {isModalOpen && (
        <TaskDetailsModal
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
          orderId={selectedTaskOrderId}
        />
      )}
      {isDriverModalOpen && (
        <DriverDetailsModal
          isOpen={isDriverModalOpen}
          onRequestClose={handleDriverModalClose}
          driver={selectedDriver}
        />
      )}
      
      {error && <div className="error">{error}</div>}
      {loading && <div className="loading">Loading...</div>}
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("remember_token");
  const user = localStorage.getItem("user");

  useEffect(() => {
    if (!token || !user) {
      navigate("/login", { replace: true });
    }
  }, [token, user, navigate]);

  return token && user ? children : null;
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ProtectedApp />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;